import React, { useEffect, useMemo, useState } from "react";
import {
  getButikkKapasiteter,
  getKapasitetsOverstyringer,
} from "../../lib/tjenestekatalogApi";
import {
  normalizeWorkshopSlug,
  isoDate,
  weekStartISO,
  getWeekNumber,
  fmtMinutes,
  prettifyStoreName,
} from "./workshopCapacityUtils";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const ALL_STORES = ["sport1royken", "sport1slemmestad", "roykensykkelverksted"];

function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function buildPastWeekStarts(weeksBack) {
  const today = new Date();
  const currentWeekStart = new Date(weekStartISO(today));
  const weeks = [];
  for (let i = 0; i < weeksBack; i++) {
    const weekStart = addDays(currentWeekStart, -i * 7);
    weeks.push({
      weekIso: isoDate(weekStart),
      weekNo: getWeekNumber(weekStart),
      year: weekStart.getFullYear(),
    });
  }
  return weeks.reverse();
}

function mergeHistoryRows(allRowArrays) {
  const byWeek = {};
  for (const rows of allRowArrays) {
    for (const row of rows) {
      if (!byWeek[row.weekIso]) {
        byWeek[row.weekIso] = { ...row };
      } else {
        const w = byWeek[row.weekIso];
        w.bruktMin = (w.bruktMin || 0) + (row.bruktMin || 0);
        w.dagMin = (w.dagMin || 0) + (row.dagMin || 0);
        w.ukeMin = (w.ukeMin || 0) + (row.ukeMin || 0);
        w.tidligukeMin = (w.tidligukeMin || 0) + (row.tidligukeMin || 0);
        w.senukeMin = (w.senukeMin || 0) + (row.senukeMin || 0);
        w.dropinMin = (w.dropinMin || 0) + (row.dropinMin || 0);
        w.fortlopendeMin = (w.fortlopendeMin || 0) + (row.fortlopendeMin || 0);
        w.omsetning = (w.omsetning || 0) + (row.omsetning || 0);
        w.antall = (w.antall || 0) + (row.antall || 0);
      }
    }
  }
  return Object.values(byWeek);
}

function WeekDetailModal({ open, onClose, title, services, showButikk }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-5xl rounded-lg bg-white shadow-xl max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between border-b px-5 py-4 bg-gray-50">
          <div>
            <h3 className="text-lg font-semibold">{title}</h3>
            <div className="text-sm text-gray-500">{services.length} service(r)</div>
          </div>
          <button onClick={onClose} className="rounded border px-3 py-1.5 text-sm hover:bg-gray-100">
            Lukk
          </button>
        </div>
        <div className="overflow-auto max-h-[75vh] p-4">
          {services.length ? (
            <table className="min-w-full text-sm">
              <thead className="bg-gray-100 text-gray-700">
                <tr>
                  <th className="px-3 py-2 text-left">Ref</th>
                  <th className="px-3 py-2 text-left">Kunde</th>
                  <th className="px-3 py-2 text-left">Type</th>
                  {showButikk && <th className="px-3 py-2 text-left">Butikk</th>}
                  <th className="px-3 py-2 text-left">Ferdigdato</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-left">Servicelinjer</th>
                  <th className="px-3 py-2 text-right">Tid</th>
                </tr>
              </thead>
              <tbody>
                {services.map((svc) => (
                  <tr key={svc.id} className="border-t align-top">
                    <td className="px-3 py-2 font-mono text-xs text-gray-600 whitespace-nowrap">{svc.serviceRef}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{svc.customerName}</td>
                    <td className="px-3 py-2 whitespace-nowrap capitalize">{svc.bookingtype || "–"}</td>
                    {showButikk && (
                      <td className="px-3 py-2 whitespace-nowrap text-gray-600">
                        {prettifyStoreName(svc.butikk)}
                      </td>
                    )}
                    <td className="px-3 py-2 whitespace-nowrap">{svc.ferdigDato}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{svc.status}</td>
                    <td className="px-3 py-2">
                      {(svc.serviceLines || []).map((l, i) => (
                        <div key={i} className="text-xs text-gray-700">
                          {l.navn} — {fmtMinutes(l.tidsbruk)}
                        </div>
                      ))}
                    </td>
                    <td className="px-3 py-2 text-right font-medium whitespace-nowrap">
                      {fmtMinutes(svc.bruktMin)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="border-t bg-gray-50">
                <tr>
                  <td colSpan={showButikk ? 7 : 6} className="px-3 py-2 text-sm font-semibold text-right">
                    Total
                  </td>
                  <td className="px-3 py-2 text-right font-bold">
                    {fmtMinutes(services.reduce((s, r) => s + r.bruktMin, 0))}
                  </td>
                </tr>
              </tfoot>
            </table>
          ) : (
            <div className="py-10 text-center text-gray-500">Ingen servicer funnet</div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function WorkshopCapacityHistoryTab({ storeSlug, weeksBack = 8 }) {
  const [loading, setLoading] = useState(true);
  const [butikker, setButikker] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [historyRows, setHistoryRows] = useState([]);
  const [details, setDetails] = useState([]);
  const [modal, setModal] = useState({ open: false, title: "", services: [] });

  const isAlle = storeSlug === "alle";

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      try {
        const [butikkerRes, overrideRows] = await Promise.all([
          getButikkKapasiteter(),
          getKapasitetsOverstyringer(),
        ]);

        const today = new Date();
        const fromDate = addDays(new Date(weekStartISO(today)), -(weeksBack - 1) * 7);
        const fromISO = isoDate(fromDate);
        const toISO = isoDate(today);

        const slugs = isAlle ? ALL_STORES : [normalizeWorkshopSlug(storeSlug)];

        const responses = await Promise.all(
          slugs.map((slug) =>
            fetch(
              `${API_BASE_URL}/workshop/capacity-history?verkstedSlug=${encodeURIComponent(slug)}&from=${fromISO}&to=${toISO}`
            ).then((r) => (r.ok ? r.json() : { rows: [], details: [] }))
          )
        );

        const mergedRows = mergeHistoryRows(responses.map((r) => r.rows || []));
        const mergedDetails = responses.flatMap((r) =>
          (r.details || []).filter((d) => !d.reason)
        );

        setButikker(butikkerRes || []);
        setOverrides(overrideRows || []);
        setHistoryRows(mergedRows);
        setDetails(mergedDetails);
      } catch (err) {
        console.error("Feil ved henting av kapasitetshistorikk:", err);
        setButikker([]);
        setOverrides([]);
        setHistoryRows([]);
        setDetails([]);
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [storeSlug, weeksBack, isAlle]);

  const rows = useMemo(() => {
    const normalizedStore = normalizeWorkshopSlug(storeSlug);

    const relevantButikker = isAlle
      ? butikker
      : butikker.filter((b) => {
          const candidates = [b?.slug, b?.butikkSlug, b?.verkstedSlug, b?.lokasjon, b?.navn, b?.butikk];
          return candidates.some((v) => normalizeWorkshopSlug(v || "") === normalizedStore);
        });

    const relevantOverrides = (overrides || []).filter((o) => {
      if (!isAlle) {
        const candidates = [o?.lokasjon, o?.slug, o?.butikkSlug, o?.verkstedSlug, o?.butikk];
        return candidates.some((v) => normalizeWorkshopSlug(v || "") === normalizedStore);
      }
      return true;
    });

    const totalUkebudsjett = relevantButikker.reduce((s, b) => s + (Number(b?.ukebudsjett) || 0), 0);
    const totalTidligukebudsjett = relevantButikker.reduce((s, b) => s + (Number(b?.tidligukebudsjett) || 0), 0);
    const totalSenukebudsjett = relevantButikker.reduce((s, b) => s + (Number(b?.senukebudsjett) || 0), 0);

    const weekStarts = buildPastWeekStarts(weeksBack);

    // Uke, tidliguke og senuke er egne, parallelle kapasitetspotter — hver
    // kan overstyres/stenges uavhengig av de andre for samme uke.
    const applyOverridesFor = (base, field, weekIso) => {
      let val = base;
      const relevant = relevantOverrides.filter(
        (o) => o?.[field] && weekStartISO(o[field]) === weekIso
      );
      for (const ov of relevant) {
        if (ov?.stengt) { val = 0; break; }
        if (typeof ov?.minutter === "number") val += ov.minutter;
      }
      return Math.max(0, val);
    };

    return weekStarts.map((w) => {
      const ukeSattAv = applyOverridesFor(totalUkebudsjett, "uke", w.weekIso);
      const tidligUkeSattAv = applyOverridesFor(totalTidligukebudsjett, "tidliguke", w.weekIso);
      const senUkeSattAv = applyOverridesFor(totalSenukebudsjett, "senuke", w.weekIso);
      const sattAvMin = ukeSattAv + tidligUkeSattAv + senUkeSattAv;

      const usedRow = (historyRows || []).find((r) => String(r.weekIso) === String(w.weekIso)) || {};

      const bruktTotalt = Number(usedRow.bruktMin || 0);
      const bruktDagMin = Number(usedRow.dagMin || 0);
      const bruktUkeMin = Number(usedRow.ukeMin || 0);
      const bruktTidligUkeMin = Number(usedRow.tidligukeMin || 0);
      const bruktSenUkeMin = Number(usedRow.senukeMin || 0);
      const bruktDropinMin = Number(usedRow.dropinMin || 0);
      const bruktFortlopendeMin = Number(usedRow.fortlopendeMin || 0);
      const antall = Number(usedRow.antall || 0);
      const ledigMin = Math.max(0, sattAvMin - bruktTotalt);

      return {
        ...w,
        sattAvMin,
        bruktTotalt,
        bruktDagMin,
        bruktUkeMin,
        bruktTidligUkeMin,
        bruktSenUkeMin,
        bruktDropinMin,
        bruktFortlopendeMin,
        antall,
        ledigMin,
        fyllingsgrad: sattAvMin > 0 ? Math.round((bruktTotalt / sattAvMin) * 100) : 0,
      };
    });
  }, [butikker, overrides, historyRows, storeSlug, weeksBack, isAlle]);

  const totals = useMemo(() => {
    return rows.reduce(
      (acc, row) => {
        acc.sattAv += Number(row.sattAvMin || 0);
        acc.brukt += Number(row.bruktTotalt || 0);
        acc.dag += Number(row.bruktDagMin || 0);
        acc.uke += Number(row.bruktUkeMin || 0);
        acc.tidliguke += Number(row.bruktTidligUkeMin || 0);
        acc.senuke += Number(row.bruktSenUkeMin || 0);
        acc.dropin += Number(row.bruktDropinMin || 0);
        acc.fortlopende += Number(row.bruktFortlopendeMin || 0);
        return acc;
      },
      { sattAv: 0, brukt: 0, dag: 0, uke: 0, tidliguke: 0, senuke: 0, dropin: 0, fortlopende: 0 }
    );
  }, [rows]);

  function openModal(row) {
    const services = details.filter((d) => d.ukeISO === row.weekIso);
    setModal({
      open: true,
      title: `Uke ${row.weekNo} / ${row.year} — ${fmtMinutes(row.bruktTotalt)}`,
      services,
    });
  }

  const currentWeekIso = weekStartISO(new Date());

  if (loading) {
    return <div className="py-10 text-center">Laster historikk…</div>;
  }

  return (
    <div className="space-y-6">
      <WeekDetailModal
        open={modal.open}
        onClose={() => setModal((m) => ({ ...m, open: false }))}
        title={modal.title}
        services={modal.services}
        showButikk={isAlle}
      />

      <div className="rounded border bg-purple-50 p-4">
        <div className="text-sm text-gray-600">Historisk kapasitet for</div>
        <div className="text-xl font-bold">
          {isAlle ? "Alle verksteder" : prettifyStoreName(storeSlug)}
        </div>
        <div className="text-sm text-gray-700 mt-1">
          Viser tidligere uker basert på ferdigdato, med avsatt og brukt kapasitet.
        </div>
      </div>

      <div className="grid md:grid-cols-4 lg:grid-cols-8 gap-4">
        <div className="rounded border p-4 bg-blue-50">
          <div className="text-sm text-gray-600">Satt av</div>
          <div className="text-xl font-bold">{fmtMinutes(totals.sattAv)}</div>
        </div>
        <div className="rounded border p-4 bg-amber-50">
          <div className="text-sm text-gray-600">Brukt totalt</div>
          <div className="text-xl font-bold">{fmtMinutes(totals.brukt)}</div>
        </div>
        <div className="rounded border p-4 bg-gray-50">
          <div className="text-sm text-gray-600">Dag</div>
          <div className="text-xl font-bold">{fmtMinutes(totals.dag)}</div>
        </div>
        <div className="rounded border p-4 bg-gray-50">
          <div className="text-sm text-gray-600">Uke</div>
          <div className="text-xl font-bold">{fmtMinutes(totals.uke)}</div>
        </div>
        <div className="rounded border p-4 bg-gray-50">
          <div className="text-sm text-gray-600">Tidliguke</div>
          <div className="text-xl font-bold">{fmtMinutes(totals.tidliguke)}</div>
        </div>
        <div className="rounded border p-4 bg-gray-50">
          <div className="text-sm text-gray-600">Senuke</div>
          <div className="text-xl font-bold">{fmtMinutes(totals.senuke)}</div>
        </div>
        <div className="rounded border p-4 bg-gray-50">
          <div className="text-sm text-gray-600">Drop-in</div>
          <div className="text-xl font-bold">{fmtMinutes(totals.dropin)}</div>
        </div>
        <div className="rounded border p-4 bg-gray-50">
          <div className="text-sm text-gray-600">Fortløpende</div>
          <div className="text-xl font-bold">{fmtMinutes(totals.fortlopende)}</div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full border rounded bg-white">
          <thead className="bg-gray-100 text-sm text-gray-700">
            <tr>
              <th className="px-4 py-2 text-left">Uke</th>
              <th className="px-4 py-2 text-left">Uke start</th>
              <th className="px-4 py-2 text-right">Satt av</th>
              <th className="px-4 py-2 text-right">Brukt totalt</th>
              <th className="px-4 py-2 text-right">Uke</th>
              <th className="px-4 py-2 text-right">Tidliguke</th>
              <th className="px-4 py-2 text-right">Senuke</th>
              <th className="px-4 py-2 text-right">Dag</th>
              <th className="px-4 py-2 text-right">Drop-in</th>
              <th className="px-4 py-2 text-right">Fortløpende</th>
              <th className="px-4 py-2 text-right">Ledig</th>
              <th className="px-4 py-2 text-right">Fyllingsgrad</th>
              <th className="px-4 py-2 text-right">Antall</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length ? (
              rows.map((row) => (
                <tr key={row.weekIso} className="border-t hover:bg-gray-50">
                  <td className="px-4 py-2 font-medium">Uke {row.weekNo} / {row.year}</td>
                  <td className="px-4 py-2">{row.weekIso}</td>
                  <td className="px-4 py-2 text-right">{fmtMinutes(row.sattAvMin)}</td>
                  <td className="px-4 py-2 text-right">{fmtMinutes(row.bruktTotalt)}</td>
                  <td className="px-4 py-2 text-right">{fmtMinutes(row.bruktUkeMin)}</td>
                  <td className="px-4 py-2 text-right">{fmtMinutes(row.bruktTidligUkeMin)}</td>
                  <td className="px-4 py-2 text-right">{fmtMinutes(row.bruktSenUkeMin)}</td>
                  <td className="px-4 py-2 text-right">{fmtMinutes(row.bruktDagMin)}</td>
                  <td className="px-4 py-2 text-right">{fmtMinutes(row.bruktDropinMin)}</td>
                  <td className="px-4 py-2 text-right">{fmtMinutes(row.bruktFortlopendeMin)}</td>
                  <td className="px-4 py-2 text-right">{row.weekIso === currentWeekIso ? "–" : fmtMinutes(row.ledigMin)}</td>
                  <td className="px-4 py-2 text-right">
                    <span
                      className={`px-2 py-1 rounded text-sm font-medium ${
                        row.fyllingsgrad >= 90
                          ? "bg-red-100 text-red-700"
                          : row.fyllingsgrad >= 70
                          ? "bg-amber-100 text-amber-700"
                          : "bg-green-100 text-green-700"
                      }`}
                    >
                      {row.fyllingsgrad} %
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right">{row.antall}</td>
                  <td className="px-4 py-2 text-right">
                    {row.bruktTotalt > 0 && (
                      <button
                        onClick={() => openModal(row)}
                        className="rounded border px-3 py-1 text-xs hover:bg-gray-100"
                      >
                        Se detaljer
                      </button>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={14} className="px-4 py-6 text-center text-gray-500">
                  Ingen historikk funnet
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
